import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const TAB_CONTENT_HEIGHT = "500px";

export const useStyles = makeStyles({
  wideDialogSurface: {
    minWidth: "680px",
    width: "680px",
  },
  dialogContent: {
    minHeight: TAB_CONTENT_HEIGHT,
    maxHeight: "70vh",
    overflow: "hidden",
    position: "relative",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  methodHelperText: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    marginTop: "0",
    marginBottom: "2px",
    lineHeight: "1.5",
  },
  methodGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[3],
  },
  methodCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.card,
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    transition: `border-color ${designTokens.transition.fast}`,
    ":hover": {
      border: `1px solid ${projectIqTokens.faint}`,
      backgroundColor: projectIqTokens.bg,
    },
  },
  methodCardSelected: {
    border: `2px solid ${projectIqTokens.teal}`,
    backgroundColor: projectIqTokens.card,
    ":hover": {
      border: `2px solid ${projectIqTokens.teal}`,
      backgroundColor: projectIqTokens.bg,
    },
  },
  methodCardEditLocked: {
    opacity: 0.35,
    cursor: "default",
    pointerEvents: "none",
    ":hover": {
      border: `1px solid ${projectIqTokens.line}`,
      backgroundColor: projectIqTokens.card,
    },
  },
  methodGridLocked: {
    pointerEvents: "none",
    cursor: "default",
  },
  methodCardRadio: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  radioCircle: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    border: `2px solid ${projectIqTokens.line}`,
    backgroundColor: projectIqTokens.card,
    flexShrink: 0,
  },
  radioCheckmark: {
    fontSize: "20px",
    color: projectIqTokens.teal,
    flexShrink: 0,
  },
  methodCardIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.bg2,
    flexShrink: 0,
    fontSize: "16px",
    color: projectIqTokens.ink3,
  },
  methodCardIconSelected: {
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.tealInk,
  },
  methodCardText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  methodCardLabel: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    lineHeight: "1.3",
  },
  methodCardDescription: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    lineHeight: "1.4",
  },

  methodCardDisabled: {
    cursor: "not-allowed",
    opacity: "0.5",
    ":hover": {
      border: `1px solid ${projectIqTokens.line}`,
      backgroundColor: projectIqTokens.card,
    },
  },

  uploadError: {
    fontSize: "11px",
    color: projectIqTokens.rust,
    marginTop: "4px",
  },

  inheritedSection: {
    marginTop: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.bg2,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  inheritedLabel: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: 0,
  },

  inheritedField: {
    display: "flex",
    gap: designTokens.spacing[3],
  },

  inheritedFieldName: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink3,
    minWidth: "80px",
    flexShrink: 0,
  },

  inheritedFieldValue: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  aiToggleSection: {
    borderTop: `1px solid ${projectIqTokens.line}`,
    display: "flex",
    flexDirection: "column",
  },

  brandingLayout: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[5],
  },

  brandingFormColumn: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },

  brandingPreviewColumn: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  brandingPreviewLabel: {
    fontSize: "11px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "0",
  },

  colorPickerRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    marginTop: designTokens.spacing[1],
  },

  colorInput: {
    width: "48px",
    height: "36px",
    padding: designTokens.spacing[1],
    border: `1px solid ${designTokens.neutral[300]}`,
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    backgroundColor: "transparent",
  },

  hexInput: {
    width: "110px",
    fontFamily: "'Consolas', 'Courier New', monospace",
  },

  demographicsDivider: {
    borderTop: `1px solid ${projectIqTokens.line}`,
    margin: `${designTokens.spacing[2]} 0`,
  },

  uploadedFileRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginTop: "4px",
  },

  logoPreview: {
    height: "32px",
    maxWidth: "120px",
    objectFit: "contain",
    borderRadius: "4px",
  },

  uploadedFileName: {
    flex: 1,
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  removeFileButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    padding: "0",
    border: "none",
    borderRadius: "4px",
    backgroundColor: "transparent",
    color: projectIqTokens.muted,
    lineHeight: "1.5",
    marginBottom: designTokens.spacing[3],
  },

  questionBankSection: {
    paddingTop: designTokens.spacing[4],
    borderTop: `1px solid ${projectIqTokens.line2}`,
    marginTop: designTokens.spacing[4],
  },
  questionBankSectionHeading: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    marginBottom: designTokens.spacing[1],
  },
  questionBankSectionHelper: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    lineHeight: "1.5",
    marginBottom: designTokens.spacing[3],
  },
  questionBankGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[2],
  },
  templateBrowseRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    marginTop: "0",
    marginBottom: "8px",
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
  },
  templateBrowseLink: {
    color: projectIqTokens.teal,
    textDecoration: "underline",
    fontSize: designTokens.fontSize.xs,
    ":hover": {
      color: projectIqTokens.tealInk,
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  templateCategoryGroup: {
    marginBottom: "4px",
  },
  templateCategoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginBottom: "2px",
  },
  templateCategoryDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  templateCategoryName: {
    fontSize: "11px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.muted,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
  },
  templateCategoryCount: {
    fontSize: "11px",
    color: projectIqTokens.faint,
  },
  templateGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[2],
  },
  templateCard: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.card,
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.ink,
    fontFamily: projectIqTokens.fontFamily,
    transition: `border-color ${designTokens.transition.fast}`,
    ":hover": {
      border: `1px solid ${projectIqTokens.faint}`,
      backgroundColor: projectIqTokens.bg,
    },
  },
  templateCardSelected: {
    border: `2px solid ${projectIqTokens.teal}`,
    ":hover": {
      border: `2px solid ${projectIqTokens.teal}`,
      backgroundColor: projectIqTokens.bg,
    },
  },
  templateCardName: {
    flex: 1,
    minWidth: 0,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    lineHeight: "1.3",
  },
  attachmentFileList: {
    marginTop: designTokens.spacing[3],
  },
  questionBankFileList: {
    marginTop: designTokens.spacing[2],
  },
  questionBankFileCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
  },
  questionBankFileIconBox: {
    width: "40px",
    height: "40px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: "rgba(47,158,110,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: projectIqTokens.green,
    fontSize: "20px",
  },
  questionBankFileMeta: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    flex: 1,
    minWidth: 0,
  },
  questionBankFileName: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  questionBankFileDetails: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
});
